/**
 * The clock everything on screen is drawn against (2026-09-02).
 *
 * User: "the characters jitter unnaturally when moving, especially when
 * running."
 *
 * ## What was actually happening
 *
 * Every entity — the local player included — is rendered by sampling
 * `RemoteInterpolator` at `estimatedServerTimeNow() - interpDelayMs()`, and
 * the camera follows that same sample. So the render clock is not a detail of
 * one entity's motion: **it is the shutter the whole world is photographed
 * through.** A wobble in it moves the floor, the walls and the body together,
 * which is exactly what "jitters unnaturally" describes.
 *
 * That estimate used to be
 *
 *     latestServerTime + (performance.now() - serverTimeReceivedAtLocal)
 *
 * with both terms reassigned on every `serverTime` patch — i.e. it **hard-reset
 * to the newest server value twenty times a second**. That is only smooth if
 * the server's clock advances by exactly as much as the client's between two
 * patches. It does not, and not because of the network:
 *
 * `setSimulationInterval` runs at `SIM_TICK_HZ` (60) and accumulates
 * `state.serverTime += dtMs`; `setPatchRate` runs at `BROADCAST_HZ` (20) on a
 * *separate* timer. So the number of sim ticks that land between two patches
 * is sometimes two and sometimes three, and the value carried by a patch jumps
 * in tick-sized quanta. Measured live against the dev server over 200 patches:
 *
 *     serverTime deltas between consecutive patches: 36 … 43 ms, then 50 … 61 ms
 *
 * A 25ms spread where a constant 50 was assumed. Every one of those
 * milliseconds was applied to the render clock as an instantaneous step.
 *
 * Replaying that recorded sequence through both implementations, as metres of
 * world displacement introduced by a single frame:
 *
 *     hard reset      walk 0.58 cm rms / 3.21 cm worst
 *                     sprint 1.48 cm rms / 8.16 cm worst
 *     this module     walk 0.04 cm rms / 0.14 cm worst
 *                     sprint 0.09 cm rms / 0.36 cm worst
 *
 * Twenty-three times smaller, and it explains the "especially when running"
 * in the report exactly: the displacement is speed × timing error, and sprint
 * is 2.5x walking pace.
 *
 * ## Rate, not position
 *
 * The correction adjusts how fast this clock *runs*, never where it *is*. A
 * proportional position nudge would still be a step, just a smaller one — and
 * a step can run the clock backwards, which shows up as a body sliding back
 * along its own path. Changing the rate instead makes going backwards
 * structurally impossible (the rate is clamped positive), and a 10% speed
 * difference over a few hundred milliseconds is not perceptible in motion the
 * way a discontinuity is.
 *
 * The snap threshold exists for the cases where smoothing is the wrong answer
 * entirely: the first patch after joining, and coming back from a backgrounded
 * tab, where `performance.now()` has advanced by seconds that the estimate must
 * simply accept rather than ease into.
 *
 * This also absorbs real network jitter, which on anything but localhost is
 * the larger term. Pure — no Colyseus, no DOM — so both bootstraps share it and
 * the numbers above are testable.
 */

export const RENDER_CLOCK = {
  /**
   * How aggressively a disagreement is worked off, as the time constant of the
   * rate correction. 400ms absorbs a 14ms quantisation step invisibly while
   * still tracking a genuinely drifting server within half a second.
   */
  CORRECTION_TAU_MS: 400,
  /** Hard ceiling on how far from real time this clock may run. */
  MAX_RATE_ADJUST: 0.1,
  /**
   * Past this the estimate is wrong rather than jittery — a join, or a tab
   * that was backgrounded — and easing in would mean seconds of visible
   * catch-up. Comfortably above the worst legitimate spread (25ms) plus any
   * plausible network jitter.
   *
   * Forwards only. See `onServerTime`.
   */
  SNAP_THRESHOLD_MS: 250,
} as const;

export class ServerRenderClock {
  private estimateMs = 0;
  private lastLocalMs: number;
  private rate = 1;

  constructor(localNowMs: number) {
    this.lastLocalMs = localNowMs;
  }

  /** The server time to draw this frame against. Never runs backwards. */
  now(localNowMs: number): number {
    return this.estimateMs + Math.max(0, localNowMs - this.lastLocalMs) * this.rate;
  }

  /**
   * A `serverTime` value straight off the wire.
   *
   * Advances the estimate to this instant first — the correction has to be
   * measured against where the clock actually *is*, not where it was when the
   * previous patch landed.
   */
  onServerTime(serverTimeMs: number, localNowMs: number): void {
    this.estimateMs = this.now(localNowMs);
    this.lastLocalMs = localNowMs;

    const errorMs = serverTimeMs - this.estimateMs;
    // Snapping is a jump, and a jump BACKWARDS is a body teleporting into its
    // own past — the single worst thing this module could put on screen, and
    // strictly worse than the jitter it exists to remove. So the snap is
    // one-directional: catching up is instant, falling back is always eased at
    // the clamped rate below. Within a room `serverTime` only ever accumulates
    // (`state.serverTime += dtMs`), so an estimate that has run ahead is the
    // only way to get here and 10% slow closes it in well under a second.
    if (errorMs > RENDER_CLOCK.SNAP_THRESHOLD_MS) {
      this.estimateMs = serverTimeMs;
      this.rate = 1;
      return;
    }
    const adjust = Math.max(
      -RENDER_CLOCK.MAX_RATE_ADJUST,
      Math.min(RENDER_CLOCK.MAX_RATE_ADJUST, errorMs / RENDER_CLOCK.CORRECTION_TAU_MS),
    );
    this.rate = 1 + adjust;
  }
}
